/**
 * 时间格式化工具函数
 * 将UTC时间转换为浏览器本地时区显示
 */

/**
 * 格式化日期时间为本地时区，精确到秒
 * @param dateString ISO格式的日期字符串
 * @returns 格式化后的日期时间字符串 (YYYY-MM-DD HH:mm:ss)
 */
export const formatDateTime = (dateString: string): string => {
  try {
    const date = new Date(dateString)
    return date.toLocaleString('zh-CN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false
    })
  } catch (error) {
    console.warn('时间格式化失败:', dateString, error)
    return dateString
  }
}

/**
 * 格式化日期，只显示日期部分
 * @param dateString ISO格式的日期字符串
 * @returns 格式化后的日期字符串 (YYYY-MM-DD)
 */
export const formatDate = (dateString: string): string => {
  try {
    const date = new Date(dateString)
    return date.toLocaleDateString('zh-CN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    })
  } catch (error) {
    console.warn('日期格式化失败:', dateString, error)
    return dateString
  }
}

/**
 * 格式化相对时间 (例如：2小时前、3天前)
 * @param dateString ISO格式的日期字符串
 * @returns 相对时间字符串
 */
export const formatRelativeTime = (dateString: string): string => {
  try {
    const date = new Date(dateString)
    const now = new Date()
    const diffMs = now.getTime() - date.getTime()
    const diffMinutes = Math.floor(diffMs / (1000 * 60))
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60))
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))

    if (diffMinutes < 1) {
      return '刚刚'
    } else if (diffMinutes < 60) {
      return `${diffMinutes}分钟前`
    } else if (diffHours < 24) {
      return `${diffHours}小时前`
    } else if (diffDays < 7) {
      return `${diffDays}天前`
    } else {
      return formatDate(dateString)
    }
  } catch (error) {
    console.warn('相对时间格式化失败:', dateString, error)
    return dateString
  }
}

/**
 * 格式化时间，返回 HH:mm:ss
 */
export const formatTime = (dateString: string): string => {
  try {
    const date = new Date(dateString)
    return date.toLocaleTimeString('zh-CN', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false
    })
  } catch (error) {
    console.warn('时间格式化失败:', dateString, error)
    return dateString
  }
}

/**
 * 格式化日期时间并附带时区信息
 */
export const formatDateTimeWithTimezone = (dateString: string): string => {
  try {
    const date = new Date(dateString)
    return new Intl.DateTimeFormat('zh-CN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
      timeZoneName: 'short'
    }).format(date)
  } catch (error) {
    console.warn('带时区格式化失败:', dateString, error)
    return dateString
  }
}

/** 获取当前用户时区 */
export const getUserTimezone = (): string => {
  return Intl.DateTimeFormat().resolvedOptions().timeZone
}

/** 获取当前时区偏移，格式为 UTC+08:00 */
export const getTimezoneOffset = (): string => {
  const offsetMinutes = new Date().getTimezoneOffset()
  const hours = Math.abs(Math.floor(offsetMinutes / 60))
  const minutes = Math.abs(offsetMinutes % 60)
  const sign = offsetMinutes <= 0 ? '+' : '-'
  return `UTC${sign}${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`
}
